import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface RevenueData {
  month: string;
  revenue: number;
  projectCount: number;
}

export interface LeadConversionData {
  status: string;
  count: number;
  percentage: number;
}

export interface LeadSourceData {
  source: string;
  count: number;
  percentage: number;
}

export interface PerformanceMetrics {
  leadConversionRate: number;
  avgProjectValue: number;
  totalRevenue: number;
  monthlyGrowth: number;
  activeProjects: number;
  completedProjects: number;
}

export function useAnalytics() {
  const { user } = useAuth();

  // Revenue Analytics
  const { data: revenueData = [], isLoading: revenueLoading } = useQuery({
    queryKey: ['revenue-analytics', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data: projects, error } = await supabase
        .from('projects')
        .select('budget, created_at, status')
        .eq('user_id', user.id)
        .not('budget', 'is', null);

      if (error) throw error;

      // Group by month and calculate revenue
      const monthlyData: Record<string, { revenue: number; count: number }> = {};
      
      projects.forEach((project) => {
        const month = new Date(project.created_at).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short' 
        });
        
        if (!monthlyData[month]) {
          monthlyData[month] = { revenue: 0, count: 0 };
        }
        
        monthlyData[month].revenue += Number(project.budget);
        monthlyData[month].count += 1;
      });

      return Object.entries(monthlyData)
        .map(([month, data]) => ({
          month,
          revenue: data.revenue,
          projectCount: data.count,
        }))
        .sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime())
        .slice(-6); // Last 6 months
    },
    enabled: !!user,
  });

  // Lead Conversion Analytics
  const { data: leadConversionData = [], isLoading: conversionLoading } = useQuery({
    queryKey: ['lead-conversion', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data: leads, error } = await supabase
        .from('leads')
        .select('status')
        .eq('user_id', user.id);

      if (error) throw error;

      const statusCounts: Record<string, number> = {};
      leads.forEach((lead) => {
        statusCounts[lead.status] = (statusCounts[lead.status] || 0) + 1;
      });

      const total = leads.length || 1;
      
      return Object.entries(statusCounts).map(([status, count]) => ({
        status: status.charAt(0).toUpperCase() + status.slice(1),
        count,
        percentage: Math.round((count / total) * 100),
      }));
    },
    enabled: !!user,
  });

  // Lead Source Analytics
  const { data: leadSourceData = [], isLoading: sourceLoading } = useQuery({
    queryKey: ['lead-sources', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data: leads, error } = await supabase
        .from('leads')
        .select('source')
        .eq('user_id', user.id);

      if (error) throw error;

      const sourceCounts: Record<string, number> = {};
      leads.forEach((lead) => {
        const source = lead.source || 'Other';
        sourceCounts[source] = (sourceCounts[source] || 0) + 1;
      });

      const total = leads.length || 1;
      
      return Object.entries(sourceCounts).map(([source, count]) => ({
        source: source.charAt(0).toUpperCase() + source.slice(1),
        count,
        percentage: Math.round((count / total) * 100),
      }));
    },
    enabled: !!user,
  });

  // Performance Metrics
  const { data: performanceMetrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['performance-metrics', user?.id],
    queryFn: async (): Promise<PerformanceMetrics> => {
      if (!user) {
        return {
          leadConversionRate: 0,
          avgProjectValue: 0,
          totalRevenue: 0,
          monthlyGrowth: 0,
          activeProjects: 0,
          completedProjects: 0,
        };
      }

      // Fetch leads and projects
      const [leadsResponse, projectsResponse] = await Promise.all([
        supabase.from('leads').select('status, estimated_value').eq('user_id', user.id),
        supabase.from('projects').select('budget, status, created_at').eq('user_id', user.id),
      ]);

      if (leadsResponse.error) throw leadsResponse.error;
      if (projectsResponse.error) throw projectsResponse.error;

      const leads = leadsResponse.data;
      const projects = projectsResponse.data;

      // Calculate metrics
      const convertedLeads = leads.filter(lead => lead.status === 'converted').length;
      const totalLeads = leads.length || 1;
      const leadConversionRate = Math.round((convertedLeads / totalLeads) * 100);

      const projectsWithBudget = projects.filter(p => p.budget !== null);
      const totalRevenue = projectsWithBudget.reduce((sum, p) => sum + Number(p.budget), 0);
      const avgProjectValue = projectsWithBudget.length > 0 
        ? Math.round(totalRevenue / projectsWithBudget.length) 
        : 0;

      const activeProjects = projects.filter(p => 
        ['planning', 'in_progress'].includes(p.status)
      ).length;
      
      const completedProjects = projects.filter(p => p.status === 'completed').length;

      // Calculate monthly growth (comparing last 2 months)
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);

      const lastMonthRevenue = projects
        .filter(p => {
          const createdAt = new Date(p.created_at);
          return createdAt >= lastMonth && createdAt < now && p.budget !== null;
        })
        .reduce((sum, p) => sum + Number(p.budget), 0);

      const previousMonthRevenue = projects
        .filter(p => {
          const createdAt = new Date(p.created_at);
          return createdAt >= twoMonthsAgo && createdAt < lastMonth && p.budget !== null;
        })
        .reduce((sum, p) => sum + Number(p.budget), 0);

      const monthlyGrowth = previousMonthRevenue > 0 
        ? Math.round(((lastMonthRevenue - previousMonthRevenue) / previousMonthRevenue) * 100)
        : 0;

      return {
        leadConversionRate,
        avgProjectValue,
        totalRevenue,
        monthlyGrowth,
        activeProjects,
        completedProjects,
      };
    },
    enabled: !!user,
  });

  return {
    revenueData,
    leadConversionData,
    leadSourceData,
    performanceMetrics,
    isLoading: revenueLoading || conversionLoading || sourceLoading || metricsLoading,
  };
}